import React, { useCallback, useEffect, useState } from 'react';
import {
  VStack, Heading, Text, Box, HStack, SimpleGrid, Select,
  Stat, StatLabel, StatNumber, Spinner, Center, useToast,
  Input, Button, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, useDisclosure, Divider, FormControl, FormLabel,
} from '@chakra-ui/react';
import { IMM_COLOR, IMM_COLOR_HOVER, NIVEL_RIESGO_COLOR } from '../_shared';
import {
  dashResumen, dashServicios, dashCasosTipoViolencia, dashCasosNivelRiesgo,
  dashPorLocalidad, dashPorRangoEdad, dashCanalizaciones, dashPsicologia,
  dashTalleres, dashAtendidas,
} from '../../../api/immApi';
import type {
  DashResumen, DashServicios, DashTipoViolencia, DashNivelRiesgo, DashLocalidad,
  DashRangoEdad, DashCanalizacion, DashPsicologia, DashTalleres, DashAtendida, DashRango,
} from '../../../api/immApi';

// ==========================================================================
// Componentes reutilizables (locales)
// ==========================================================================

type BarRow = { label: string; value: number; color?: string; secondary?: number };

/** Lista de barras horizontal, sin librerías externas. */
const BarList = ({ data }: { data: BarRow[] }) => {
  if (!data || data.length === 0) {
    return <Text fontSize="sm" color="gray.400">Sin datos</Text>;
  }
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <VStack align="stretch" spacing={2}>
      {data.map((row, i) => (
        <HStack key={`${row.label}-${i}`} spacing={3} align="center">
          <Text
            fontSize="xs" color="gray.600" noOfLines={1} flexShrink={0}
            w={{ base: '90px', md: '130px' }} title={row.label}
          >
            {row.label}
          </Text>
          <Box flex="1" bg="gray.100" borderRadius="sm" overflow="hidden">
            <Box
              h="20px" borderRadius="sm" minW="4px"
              bg={row.color ? `${row.color}.400` : IMM_COLOR}
              width={`${(row.value / max) * 100}%`}
            />
          </Box>
          <HStack spacing={1} flexShrink={0} justify="flex-end" w="70px">
            <Text fontSize="sm" fontWeight="bold" color="gray.700">{row.value}</Text>
            {typeof row.secondary === 'number' && (
              <Text fontSize="xs" color="gray.400">({row.secondary})</Text>
            )}
          </HStack>
        </HStack>
      ))}
    </VStack>
  );
};

/** Tarjeta contenedora con título institucional. */
const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
    <Heading size="sm" color={IMM_COLOR} mb={3}>{title}</Heading>
    {children}
  </Box>
);

/**
 * Tarjeta de estadística (Stat) con borde superior institucional.
 * Si `onClick` está definido, la tarjeta es interactiva (abre su desglose).
 */
const StatCard = ({ label, value, onClick }: { label: string; value: number; onClick?: () => void }) => (
  <Box
    bg="white" borderRadius="lg" boxShadow="sm" borderTop={`3px solid ${IMM_COLOR}`} p={4}
    onClick={onClick}
    cursor={onClick ? 'pointer' : 'default'}
    role={onClick ? 'button' : undefined}
    transition="all 0.15s ease"
    _hover={onClick ? { boxShadow: 'md', transform: 'translateY(-2px)' } : undefined}
  >
    <Stat>
      <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
      <StatNumber color={IMM_COLOR}>{value}</StatNumber>
      {onClick && <Text fontSize="10px" color="gray.400" mt={1}>Ver desglose</Text>}
    </Stat>
  </Box>
);

// ==========================================================================
// Drill-down (desglose anonimizado de cada KPI)
// ==========================================================================

/** Sección de un desglose: un título opcional + barras + nota. */
type DrillSection = { heading?: string; data: BarRow[]; footnote?: string };
type Drill = { title: string; note?: string; sections: DrillSection[] };

// ==========================================================================
// Dashboard
// ==========================================================================

type Periodo = 'dia' | 'mes' | 'anio';

const Dashboard: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState<Periodo>('mes');

  // Filtro de fecha: borrador (inputs) + rango aplicado que dispara el re-fetch.
  const [desdeInput, setDesdeInput] = useState('');
  const [hastaInput, setHastaInput] = useState('');
  const [rango, setRango] = useState<DashRango>({});

  const [drill, setDrill] = useState<Drill | null>(null);

  const [resumen, setResumen] = useState<DashResumen | null>(null);
  const [servicios, setServicios] = useState<DashServicios | null>(null);
  const [tipoViolencia, setTipoViolencia] = useState<DashTipoViolencia[]>([]);
  const [nivelRiesgo, setNivelRiesgo] = useState<DashNivelRiesgo[]>([]);
  const [porLocalidad, setPorLocalidad] = useState<DashLocalidad[]>([]);
  const [porRangoEdad, setPorRangoEdad] = useState<DashRangoEdad[]>([]);
  const [canalizaciones, setCanalizaciones] = useState<DashCanalizacion[]>([]);
  const [psicologia, setPsicologia] = useState<DashPsicologia | null>(null);
  const [talleres, setTalleres] = useState<DashTalleres | null>(null);
  const [atendidas, setAtendidas] = useState<DashAtendida[]>([]);

  // Parámetros de rango que se envían a cada endpoint (sin claves vacías).
  const rangoParams: DashRango = {};
  if (rango.desde) rangoParams.desde = rango.desde;
  if (rango.hasta) rangoParams.hasta = rango.hasta;

  // Carga de todos los agregados aplicando el rango de fecha vigente.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [
          rResumen, rServicios, rTipoViolencia, rNivelRiesgo, rLocalidad,
          rRangoEdad, rCanalizaciones, rPsicologia, rTalleres, rAtendidas,
        ] = await Promise.all([
          dashResumen(rangoParams),
          dashServicios(rangoParams),
          dashCasosTipoViolencia(rangoParams),
          dashCasosNivelRiesgo(rangoParams),
          dashPorLocalidad(rangoParams),
          dashPorRangoEdad(rangoParams),
          dashCanalizaciones(rangoParams),
          dashPsicologia(rangoParams),
          dashTalleres(rangoParams),
          dashAtendidas({ periodo, ...rangoParams }),
        ]);
        if (cancelled) return;
        setResumen(rResumen);
        setServicios(rServicios);
        setTipoViolencia(rTipoViolencia);
        setNivelRiesgo(rNivelRiesgo);
        setPorLocalidad(rLocalidad);
        setPorRangoEdad(rRangoEdad);
        setCanalizaciones(rCanalizaciones);
        setPsicologia(rPsicologia);
        setTalleres(rTalleres);
        setAtendidas(rAtendidas);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: 'Error al cargar el tablero',
            description: 'No se pudieron obtener los indicadores.',
            status: 'error', duration: 5000, isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango]);

  // Recarga solo la serie "mujeres atendidas" al cambiar el periodo.
  useEffect(() => {
    if (loading) return; // evita doble carga en el montaje inicial
    let cancelled = false;
    dashAtendidas({ periodo, ...rangoParams })
      .then((d) => { if (!cancelled) setAtendidas(d); })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Error al actualizar el periodo',
            status: 'error', duration: 4000, isClosable: true,
          });
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const aplicarFiltro = useCallback(() => {
    setRango({ desde: desdeInput || undefined, hasta: hastaInput || undefined });
  }, [desdeInput, hastaInput]);

  const limpiarFiltro = useCallback(() => {
    setDesdeInput('');
    setHastaInput('');
    setRango({});
  }, []);

  const abrirDrill = useCallback((d: Drill) => { setDrill(d); onOpen(); }, [onOpen]);

  // ---- Desgloses anonimizados por KPI (solo agregados, sin datos personales) ----
  const serviciosBars: BarRow[] = servicios ? [
    { label: 'Orientación', value: servicios.orientacion },
    { label: 'Asesoría', value: servicios.asesoria },
    { label: 'Acompañamiento', value: servicios.acompanamiento },
    { label: 'Canalización', value: servicios.canalizacion },
  ] : [];
  const serviciosFootnote = `Total de atenciones en el periodo: ${servicios?.totalAtenciones ?? 0}`;

  const atendidasBars: BarRow[] = atendidas.map((a) => ({
    label: a.periodo, value: a.atenciones, secondary: a.mujeresUnicas,
  }));
  const localidadBars: BarRow[] = porLocalidad.map((l) => ({ label: l.localidad, value: l.total }));
  const rangoEdadBars: BarRow[] = porRangoEdad.map((r) => ({ label: r.rango, value: r.total }));
  const tipoViolenciaBars: BarRow[] = tipoViolencia.map((t) => ({ label: t.tipoViolencia, value: t.total }));
  const nivelRiesgoBars: BarRow[] = nivelRiesgo.map((n) => ({
    label: n.nivelRiesgo, value: n.total, color: NIVEL_RIESGO_COLOR[n.nivelRiesgo],
  }));
  const canalizacionesBars: BarRow[] = canalizaciones.map((c) => ({ label: c.dependencia, value: c.total }));
  const citasBars: BarRow[] = (psicologia?.citasPorEstatus ?? []).map((c) => ({ label: c.estatus, value: c.total }));
  const inscripcionesBars: BarRow[] = (talleres?.inscripcionesPorEstatus ?? []).map((c) => ({ label: c.estatus, value: c.total }));

  const drillExpedientes: Drill = {
    title: 'Expedientes — desglose',
    sections: [
      { heading: 'Por localidad', data: localidadBars },
      { heading: 'Por rango de edad', data: rangoEdadBars },
    ],
  };
  const drillCasos: Drill = {
    title: 'Casos — desglose',
    sections: [
      { heading: 'Por tipo de violencia', data: tipoViolenciaBars },
      { heading: 'Por nivel de riesgo', data: nivelRiesgoBars },
    ],
  };
  const drillAtenciones: Drill = {
    title: 'Atenciones — desglose',
    sections: [
      { heading: 'Por periodo', data: atendidasBars, footnote: 'Valor: atenciones. Entre paréntesis: mujeres únicas.' },
      { heading: 'Por tipo de servicio', data: serviciosBars, footnote: serviciosFootnote },
    ],
  };
  const drillCanalizaciones: Drill = {
    title: 'Canalizaciones — desglose',
    sections: [{ heading: 'Por dependencia destino', data: canalizacionesBars }],
  };
  const drillServicios: Drill = {
    title: 'Servicios en atenciones — desglose',
    sections: [{ heading: 'Servicios brindados', data: serviciosBars, footnote: serviciosFootnote }],
  };
  const drillPsicologia: Drill = {
    title: 'Psicología — desglose',
    sections: [{ heading: 'Citas por estatus', data: citasBars, footnote: `Sesiones registradas: ${psicologia?.sesiones ?? 0}` }],
  };
  const drillTalleres: Drill = {
    title: 'Talleres — desglose',
    sections: [{ heading: 'Inscripciones por estatus', data: inscripcionesBars, footnote: `Talleres: ${talleres?.totalTalleres ?? 0} · Inscripciones: ${talleres?.totalInscripciones ?? 0}` }],
  };
  const drillLocalidad: Drill = { title: 'Expedientes por localidad', sections: [{ heading: 'Por localidad', data: localidadBars }] };
  const drillRangoEdad: Drill = { title: 'Expedientes por rango de edad', sections: [{ heading: 'Por rango de edad', data: rangoEdadBars }] };
  const drillOrdenes: Drill = {
    title: 'Órdenes de protección vigentes',
    note: 'Conteo directo de órdenes con estatus vigente en el periodo. No cuenta con un desglose adicional anonimizado.',
    sections: [],
  };
  const drillSolicitudes: Drill = {
    title: 'Solicitudes nuevas recibidas',
    note: 'Conteo directo de solicitudes en estatus "Recibida" dentro del periodo. No cuenta con un desglose adicional anonimizado.',
    sections: [],
  };

  if (loading) {
    return (
      <Center py={20}>
        <Spinner size="xl" thickness="4px" color={IMM_COLOR} />
      </Center>
    );
  }

  return (
    <VStack spacing={6} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={4}>
      {/* 0) Filtro de fecha */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Heading size="sm" color={IMM_COLOR}>Filtro por rango de fecha</Heading>
          {(rango.desde || rango.hasta) && (
            <Text fontSize="xs" color="gray.500">
              Mostrando: {rango.desde || 'inicio'} — {rango.hasta || 'hoy'}
            </Text>
          )}
        </HStack>
        <HStack flexWrap="wrap" align="flex-end" spacing={4}>
          <FormControl maxW="200px">
            <FormLabel fontSize="xs" color="gray.500" mb={1}>Desde</FormLabel>
            <Input type="date" size="sm" value={desdeInput} max={hastaInput || undefined}
              onChange={(e) => setDesdeInput(e.target.value)} />
          </FormControl>
          <FormControl maxW="200px">
            <FormLabel fontSize="xs" color="gray.500" mb={1}>Hasta</FormLabel>
            <Input type="date" size="sm" value={hastaInput} min={desdeInput || undefined}
              onChange={(e) => setHastaInput(e.target.value)} />
          </FormControl>
          <HStack spacing={2}>
            <Button
              size="sm" color="white" bg={IMM_COLOR} _hover={{ bg: IMM_COLOR_HOVER }}
              onClick={aplicarFiltro}
            >
              Aplicar
            </Button>
            <Button
              size="sm" variant="outline" color={IMM_COLOR} borderColor={IMM_COLOR}
              _hover={{ bg: 'gray.50' }} onClick={limpiarFiltro}
              isDisabled={!rango.desde && !rango.hasta && !desdeInput && !hastaInput}
            >
              Limpiar
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* 1) Resumen general (KPIs interactivos) */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
        <StatCard label="Expedientes" value={resumen?.expedientes ?? 0} onClick={() => abrirDrill(drillExpedientes)} />
        <StatCard label="Casos abiertos" value={resumen?.casosAbiertos ?? 0} onClick={() => abrirDrill(drillCasos)} />
        <StatCard label="Atenciones" value={resumen?.atenciones ?? 0} onClick={() => abrirDrill(drillAtenciones)} />
        <StatCard label="Canalizaciones" value={resumen?.canalizaciones ?? 0} onClick={() => abrirDrill(drillCanalizaciones)} />
        <StatCard label="Órdenes vigentes" value={resumen?.ordenesVigentes ?? 0} onClick={() => abrirDrill(drillOrdenes)} />
        <StatCard label="Solicitudes nuevas" value={resumen?.solicitudesRecibidas ?? 0} onClick={() => abrirDrill(drillSolicitudes)} />
      </SimpleGrid>

      {/* 2) Servicios + psicología + talleres (KPIs interactivos) */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
        <StatCard label="Orientación" value={servicios?.orientacion ?? 0} onClick={() => abrirDrill(drillServicios)} />
        <StatCard label="Asesoría" value={servicios?.asesoria ?? 0} onClick={() => abrirDrill(drillServicios)} />
        <StatCard label="Acompañamiento" value={servicios?.acompanamiento ?? 0} onClick={() => abrirDrill(drillServicios)} />
        <StatCard label="Canalización" value={servicios?.canalizacion ?? 0} onClick={() => abrirDrill(drillServicios)} />
        <StatCard label="Sesiones psicología" value={psicologia?.sesiones ?? 0} onClick={() => abrirDrill(drillPsicologia)} />
        <StatCard label="Talleres" value={talleres?.totalTalleres ?? 0} onClick={() => abrirDrill(drillTalleres)} />
        <StatCard label="Inscripciones a talleres" value={talleres?.totalInscripciones ?? 0} onClick={() => abrirDrill(drillTalleres)} />
      </SimpleGrid>

      {/* 3) Mujeres atendidas por periodo */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Heading size="sm" color={IMM_COLOR}>Mujeres atendidas por periodo</Heading>
          <Select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as Periodo)}
            maxW="180px" size="sm"
          >
            <option value="dia">Por día</option>
            <option value="mes">Por mes</option>
            <option value="anio">Por año</option>
          </Select>
        </HStack>
        <BarList data={atendidasBars} />
        <Text fontSize="xs" color="gray.400" mt={2}>
          Valor: atenciones. Entre paréntesis: mujeres únicas.
        </Text>
      </Box>

      {/* 4) Distribuciones */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Card title="Casos por tipo de violencia">
          <BarList data={tipoViolenciaBars} />
        </Card>

        <Card title="Casos por nivel de riesgo">
          <BarList data={nivelRiesgoBars} />
        </Card>

        <Card title="Expedientes por localidad">
          <BarList data={localidadBars} />
        </Card>

        <Card title="Distribución por rango de edad">
          <BarList data={rangoEdadBars} />
        </Card>

        <Card title="Canalizaciones por dependencia">
          <BarList data={canalizacionesBars} />
        </Card>

        <Card title="Citas de psicología por estatus">
          <BarList data={citasBars} />
        </Card>

        <Card title="Inscripciones a talleres por estatus">
          <BarList data={inscripcionesBars} />
        </Card>
      </SimpleGrid>

      {/* Modal de desglose (drill-down anonimizado) */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={IMM_COLOR}>{drill?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {(rango.desde || rango.hasta) && (
              <Text fontSize="xs" color="gray.500" mb={3}>
                Periodo: {rango.desde || 'inicio'} — {rango.hasta || 'hoy'}
              </Text>
            )}
            {drill?.note && (
              <Text fontSize="sm" color="gray.600" mb={3}>{drill.note}</Text>
            )}
            <VStack align="stretch" spacing={5}>
              {(drill?.sections ?? []).map((s, i) => (
                <Box key={i}>
                  {i > 0 && <Divider mb={4} />}
                  {s.heading && (
                    <Text fontSize="xs" fontWeight="bold" color={IMM_COLOR}
                      textTransform="uppercase" letterSpacing="wide" mb={2}>
                      {s.heading}
                    </Text>
                  )}
                  <BarList data={s.data} />
                  {s.footnote && <Text fontSize="xs" color="gray.400" mt={2}>{s.footnote}</Text>}
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default Dashboard;
